import React, { useState } from "react";

import { faCog } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Modal, Spinner, Tag } from "@samskip/frontend-components";
import { useTranslation } from "react-i18next";

import { LinkOpenTypes } from "@assets/constants";
import { MenuItem } from "@assets/types";
import { NavMenuUtilities, WindowUtilities } from "@assets/utilities";
import { classNames } from "@assets/utilities/classNameUtilities";
import Menu from "@components/Menu/menu";

type NavigationProps = { menuItems?: MenuItem[]; selectedMenu?: string };

const Navigation = ({ menuItems = [], selectedMenu }: NavigationProps) => {
  const { t } = useTranslation();
  const [openedMenu, setOpenedMenu] = useState<number | null>(null);
  const [isIFrameModalOpen, setIsIFrameModalOpen] = useState(false);
  const [iFrameLink, setIFrameLink] = useState("");

  const openMenu = (menuId: number) => {
    setOpenedMenu(menuId);
  };

  const closeAllMenu = () => {
    setOpenedMenu(null);
  };
  const openIframe = () => {
    setIsIFrameModalOpen(true);
  };

  const closeIframe = () => {
    setIsIFrameModalOpen(false);
  };

  // To be able to tackle all types of window openings: the same page, new tab, iframe or new window)
  // NOTE: Most probably should be handled by the anchor element itself, but need to keep consistency wit Service Web v1
  const handleMenuItemClick = (item: MenuItem) => {
    const itemLink = NavMenuUtilities.constructLink(item);
    switch (item.ExternalLink) {
      // Open in MainServiceWeb (in the main Service web V1, Open in the same page)
      default:
      case LinkOpenTypes.mainServiceWeb:
        WindowUtilities.navigatePage(itemLink);
        break;
      // Open in a new window
      case LinkOpenTypes.openPopupWindow:
        WindowUtilities.openPopup(itemLink, item.MenuName, 1020, 750);
        break;
      // Open in a iframe
      case LinkOpenTypes.openIframe:
        setIFrameLink(itemLink);
        openIframe();
        break;
      // Open in a new tab
      case LinkOpenTypes.openTabOrWindow:
        WindowUtilities.openTabOrWindow(itemLink);
        break;
    }
  };

  const getSubmenu = (subMenu: MenuItem) => {
    return (
      <Menu.Item
        className="flex gap-2"
        key={subMenu.MenuID}
        lined
        onClick={() => handleMenuItemClick(subMenu)}
        size="medium"
      >
        {t(subMenu.Translate)}
        {subMenu.IsNew && (
          <Tag color="secondary" size="small">
            {t("LABEL_NEW")}
          </Tag>
        )}
      </Menu.Item>
    );
  };

  const selectedClasses = `text-primary
  after:content-[''] after:h-0 after:w-0 after:border-transparent after:border-t-secondary after:absolute after:top-7 after:left-[calc(50%-4px)] after:border-y-8 after:border-x-4`;

  return (
    <div className="flex items-baseline gap-5 pt-5">
      {menuItems?.map((menuItem) => (
        <div key={menuItem.MenuID}>
          <div
            className="uppercase font-bold transition-colors hover:text-primary cursor-pointer relative"
            onClick={() => openMenu(menuItem.MenuID)}
            onMouseOut={closeAllMenu}
            onMouseOver={() => openMenu(menuItem.MenuID)}
          >
            <div
              className={classNames(
                "flex items-center gap-1.5 pb-3.5 text-base",
                selectedMenu === menuItem.Site && selectedClasses
              )}
              onClick={() => handleMenuItemClick(menuItem)}
            >
              {menuItem.MenuName === "Settings" ? (
                <FontAwesomeIcon icon={faCog} />
              ) : (
                t(menuItem.Translate)
              )}
            </div>
          </div>
          {menuItem.children && menuItem.children?.length > 0 && (
            <Menu
              isOpen={openedMenu === menuItem.MenuID}
              key={menuItem.MenuID}
              onMouseOut={closeAllMenu}
              onMouseOver={() => openMenu(menuItem.MenuID)}
            >
              {menuItem.children.map((subMenu) => getSubmenu(subMenu))}
            </Menu>
          )}
        </div>
      ))}
      {/* Needed to open iframe menu links. */}
      <Modal
        closeIcon
        closeOnOutsideClick
        onClose={closeIframe}
        open={isIFrameModalOpen}
      >
        <div className="relative h-96">
          <div className="absolute top-0 w-full h-full">
            <Spinner fullHeight fullWidth />
          </div>
          <iframe className="w-full h-full" src={iFrameLink} />
        </div>
      </Modal>
    </div>
  );
};

export default Navigation;
